import { blue, lightBlue, lightYellow } from '@synoptic/ui-kit/palette.js';
import { themeVars } from '@synoptic/ui-kit/theme/vars.js';
import { borderRadius, fonts, space } from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { globalStyle, style, StyleRule } from '@vanilla-extract/css';
import { EditorThemeClasses, EditorThemeClassName } from 'lexical';

type ReplaceThemeClassName<T> = T extends EditorThemeClassName
  ? StyleRule
  : T extends Record<string, unknown>
    ? { [K in keyof T]: ReplaceThemeClassName<T[K]> }
    : T;

type EditorThemeStyles = ReplaceThemeClassName<EditorThemeClasses>;

const boldStyle = typography['titleMd'];

const blockMargin = {
  marginBlockStart: space.s12,
  marginBlockEnd: space.s12,
  ':first-child': {
    marginBlockStart: 0,
  },
  ':last-child': {
    marginBlockEnd: 0,
  },
} as const;

export const baseTextEditorStyles = {
  root: {
    ...typography['bodyLg'],

    height: '100%',
    outline: 'none',
    selectors: {
      '&[contenteditable="true"]': {
        cursor: 'text',
      },
    },
  },
  hr: {
    ...blockMargin,
    border: 0,
    height: 1,
    backgroundColor: themeVars.container.border.default,
  },
  hrSelected: {
    outline: `2px solid ${lightBlue[500]}`,
    outlineOffset: '-1px',
  },
  link: {
    color: blue[400],
    ':hover': {
      textDecoration: 'underline',
    },
  },
  heading: {
    h1: {
      ...typography['titleMd'],
      fontSize: 18,
    },
    h2: boldStyle,
    h3: boldStyle,
    h4: boldStyle,
    h5: boldStyle,
    h6: boldStyle,
  },
  quote: {
    ...typography['bodyMd'],
    padding: `${space.s8}px ${space.s12}px ${space.s8}px 11px`,
    borderLeft: `5px solid ${themeVars.link.idle}`,
    borderRadius: `${space.s6}px ${space.s12}px ${space.s12}px ${space.s6}px`,
    backgroundColor: 'rgba(221, 212, 245, 0.2)',
    ...blockMargin,
  },
  list: {
    ol: { margin: 0, padding: `0 0 0 ${space.s24}px` },
    ul: { margin: 0, padding: `0 0 0 ${space.s24}px` },
  },
  text: {
    italic: {
      fontFamily: fonts.sansItalic,
      fontStyle: 'italic',
    },
    bold: boldStyle,
    underline: {
      textDecoration: 'underline',
    },
    strikethrough: {
      textDecoration: 'line-through',
    },
    underlineStrikethrough: {
      textDecoration: 'underline line-through',
    },
    subscript: {
      verticalAlign: 'sub',
    },
    superscript: {
      verticalAlign: 'super',
    },
    code: {
      backgroundColor: themeVars.container.secondary.background.idle,
      padding: `0 ${space.s6}px`,
      borderRadius: `${borderRadius.r4}px`,
    },
    highlight: {
      backgroundColor: `var(--lexical-highlight-bg, ${themeVars.color.surface.aux.lightYellow.default})`,
      borderBottom: `1px solid var(--lexical-highlight-border, ${lightYellow[500]})`,
      color: themeVars.color.text.primary,
      selectors: {
        'a &': {
          color: 'inherit',
        },
      },
    },
  },
  code: {
    backgroundColor: themeVars.container.secondary.background.idle,
    padding: `${space.s6}px ${space.s8}px`,
    borderRadius: `${borderRadius.r4}px`,
    outline: `1px solid ${themeVars.container.border.secondary}`,
    outlineOffset: '-1px',
    display: 'block',
    ...blockMargin,
  },
} satisfies EditorThemeStyles;

const bold = style(baseTextEditorStyles.text.bold);

export const baseTextEditorTheme = {
  root: style(baseTextEditorStyles.root),

  link: style(baseTextEditorStyles.link),
  heading: {
    h1: style(baseTextEditorStyles.heading.h1),
    h2: bold,
    h3: bold,
    h4: bold,
    h5: bold,
    h6: bold,
  },
  hr: style(baseTextEditorStyles.hr),
  hrSelected: style(baseTextEditorStyles.hrSelected),
  quote: style(baseTextEditorStyles.quote),
  list: {
    ol: style(baseTextEditorStyles.list.ol),
    ul: style(baseTextEditorStyles.list.ul),
  },
  text: {
    italic: style(baseTextEditorStyles.text.italic),
    bold: bold,
    underline: style(baseTextEditorStyles.text.underline),
    strikethrough: style(baseTextEditorStyles.text.strikethrough),
    underlineStrikethrough: style(
      baseTextEditorStyles.text.underlineStrikethrough,
    ),
    subscript: style(baseTextEditorStyles.text.subscript),
    superscript: style(baseTextEditorStyles.text.superscript),
    code: style(baseTextEditorStyles.text.code),
    highlight: style(baseTextEditorStyles.text.highlight),
  },
  code: style(baseTextEditorStyles.code),
} satisfies EditorThemeClasses;

globalStyle('mark', {
  color: 'unset',
});
